<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useMapGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import BackButton from 'dashboard/components/widgets/BackButton.vue';
import SettingsPageLayout from 'dashboard/components-next/captain/SettingsPageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomToolSelectionCard from 'dashboard/components-next/captain/pageComponents/assistant/custom-tools/CustomToolSelectionCard.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const getters = useStoreGetters();

const assistantId = Number(route.params.assistantId);
const searchQuery = ref('');
const selectedToolIds = ref([]);
const originalToolIds = ref([]);
const isUpdating = ref(false);

const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetchingAssistant = computed(() => assistantUiFlags.value.fetchingItem);
const assistant = computed(() =>
  getters['captainAssistants/getRecord'].value(assistantId)
);

const customToolsUiFlags = useMapGetter('captainCustomTools/getUIFlags');
const isFetchingTools = computed(() => customToolsUiFlags.value.fetchingList);
const allCustomTools = useMapGetter('captainCustomTools/getRecords');

const enabledCustomTools = computed(() =>
  allCustomTools.value.filter(tool => tool.enabled)
);

const filteredTools = computed(() => {
  if (!searchQuery.value) return enabledCustomTools.value;
  return picoSearch(enabledCustomTools.value, searchQuery.value, [
    'title',
    'description',
  ]);
});

const isFetching = computed(
  () => isFetchingAssistant.value || isFetchingTools.value
);

const breadcrumbItems = computed(() => [
  {
    label: t('CAPTAIN.ASSISTANTS.SETTINGS.BREADCRUMB.ASSISTANT'),
    routeName: 'captain_assistants_index',
  },
  { label: assistant.value?.name, routeName: 'captain_assistants_edit' },
  {
    label: t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.BREADCRUMB.TITLE'),
    routeName: 'captain_assistants_custom_tools_index',
  },
]);

const handleToggle = toolId => {
  const index = selectedToolIds.value.indexOf(toolId);
  if (index > -1) {
    selectedToolIds.value.splice(index, 1);
  } else {
    selectedToolIds.value.push(toolId);
  }
};

const hasChanges = computed(() => {
  const added = selectedToolIds.value.filter(
    id => !originalToolIds.value.includes(id)
  );
  const removed = originalToolIds.value.filter(
    id => !selectedToolIds.value.includes(id)
  );
  return added.length > 0 || removed.length > 0;
});

const handleUpdate = async () => {
  if (!hasChanges.value) return;

  isUpdating.value = true;

  try {
    await store.dispatch('captainAssistants/updateCustomTools', {
      assistantId,
      customToolIds: selectedToolIds.value,
    });

    originalToolIds.value = [...selectedToolIds.value];

    useAlert(t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.UPDATE_SUCCESS'));
  } catch (error) {
    selectedToolIds.value = [...originalToolIds.value];

    const errorMessage =
      error?.response?.data?.error ||
      t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.UPDATE_ERROR');
    useAlert(errorMessage);
  } finally {
    isUpdating.value = false;
  }
};

onBeforeMount(async () => {
  await store.dispatch('captainAssistants/show', assistantId);

  await store.dispatch('captainCustomTools/get', {});

  const customToolIds = assistant.value?.custom_tool_ids || [];
  selectedToolIds.value = [...customToolIds];
  originalToolIds.value = [...customToolIds];
});
</script>

<template>
  <SettingsPageLayout
    :breadcrumb-items="breadcrumbItems"
    :is-fetching="isFetching"
    class="[&>div]:max-w-[80rem]"
  >
    <template #headerTitle>
      <div class="flex flex-row items-center gap-4">
        <BackButton compact />
        <span
          class="flex items-center gap-1 text-lg font-medium text-n-slate-12"
        >
          {{ assistant.name }}
          <span class="i-lucide-chevron-right text-xl text-n-slate-10" />
          {{ t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.TITLE') }}
        </span>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.TITLE')"
            :description="t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.DESCRIPTION')"
          />

          <div class="flex items-center gap-4">
            <div class="flex-1 min-w-0">
              <Input
                v-model="searchQuery"
                :placeholder="
                  t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.SEARCH_PLACEHOLDER')
                "
              />
            </div>

            <Button
              :label="t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.UPDATE_BUTTON')"
              :disabled="!hasChanges"
              :loading="isUpdating"
              @click="handleUpdate"
            />
          </div>

          <div v-if="filteredTools.length > 0" class="flex flex-col gap-4">
            <CustomToolSelectionCard
              v-for="tool in filteredTools"
              :id="tool.id"
              :key="tool.id"
              :title="tool.title"
              :description="tool.description"
              :is-selected="selectedToolIds.includes(tool.id)"
              @toggle="handleToggle"
            />
          </div>

          <div
            v-else-if="!isFetching && enabledCustomTools.length === 0"
            class="flex items-center justify-center p-8 text-center"
          >
            <span class="text-sm text-n-slate-11">
              {{ t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.NO_ENABLED_TOOLS') }}
            </span>
          </div>

          <div
            v-else-if="!isFetching && searchQuery"
            class="flex items-center justify-center p-8 text-center"
          >
            <span class="text-sm text-n-slate-11">
              {{ t('CAPTAIN.ASSISTANTS.CUSTOM_TOOLS.SEARCH_EMPTY_MESSAGE') }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </SettingsPageLayout>
</template>
